import {
  type RelativeDateTimeMatcherOption,
  type SelectedFilterField,
  isRelativeDateTimeMatcherOption,
} from '@cdw-ae/filter-bar-interfaces';
import {
  millisecondsPerDay,
  millisecondsPerHour,
  millisecondsPerWeek,
} from '@cdw-ae/common-data';

/**
 * Mirrors relativeDateTimeLookbackMs in typeorm.data-repo.ts. Keyed off the same
 * RelativeDateTimeMatcherOption exported by filter-bar-interfaces (see EDUS-2915) so this
 * client-side matcher can't drift from the SQL layer's option list the way the two did before.
 */
const relativeDateTimeLookbackMs: Record<RelativeDateTimeMatcherOption, number> = {
  lastHour: millisecondsPerHour,
  lastDay: millisecondsPerDay,
  lastWeek: millisecondsPerWeek,
};

/**
 * Filters an in-memory array of items against a CdwSearchGrid filter bar's
 * active filter selections.
 *
 * For ICdwSearchGridDataService implementations backed by a dataset that's
 * fully fetched up front (small, already in memory, no server-side query
 * support to push activeFilters into) rather than a real backend query.
 */
export function applyActiveFilters<T>(
  items: T[],
  activeFilters: SelectedFilterField[] | undefined
): T[] {
  if (!activeFilters || activeFilters.length === 0) {
    return items;
  }

  return items.filter((item) => matchesActiveFilters(item, activeFilters));
}

/**
 * True if a single item satisfies every active filter (AND logic).
 */
export function matchesActiveFilters<T>(
  item: T,
  activeFilters: SelectedFilterField[]
): boolean {
  return activeFilters.every((filter) => matchesFilter(item, filter));
}

function matchesFilter<T>(item: T, filter: SelectedFilterField): boolean {
  // Filter field names are dynamic (chosen by the consumer's FilterBarField
  // config), so there's no way to type this access statically against T.
  const fieldValue = (item as Record<string, unknown>)[filter.field.name];
  const filterValue = filter.matchValue;

  if (fieldValue === null || fieldValue === undefined) {
    return false;
  }

  switch (filter.selectedMatcher.name) {
    case 'dateTime':
      return matchDateTime(fieldValue, filterValue);
    case 'numberMatch':
      return matchNumber(fieldValue, filterValue);
    case 'ouTreePicker':
      return matchOuTreePicker(fieldValue, filterValue);
    case 'startsWith':
      return String(fieldValue)
        .toLowerCase()
        .startsWith(String(filterValue).toLowerCase());
    case 'fullMatch':
    case 'equals':
      return String(fieldValue).toLowerCase() === String(filterValue).toLowerCase();
    case 'contains':
    default:
      return String(fieldValue)
        .toLowerCase()
        .includes(String(filterValue).toLowerCase());
  }
}

/**
 * Matches the `numberMatch` matcher's `{ operator, value }` matchValue shape
 * (see libs/filter-bar-matcher-configs/src/lib/number.config.ts).
 */
function matchNumber(fieldValue: unknown, filterValue: unknown): boolean {
  if (
    typeof filterValue !== 'object' ||
    filterValue === null ||
    !('operator' in filterValue) ||
    !('value' in filterValue)
  ) {
    return false;
  }

  const { operator, value } = filterValue as {
    operator: string;
    value: string | number;
  };
  const fieldNum = Number(fieldValue);
  const filterNum = Number(value);
  if (Number.isNaN(fieldNum) || Number.isNaN(filterNum)) {
    return false;
  }

  switch (operator) {
    case '=':
      return fieldNum === filterNum;
    case '>':
      return fieldNum > filterNum;
    case '<':
      return fieldNum < filterNum;
    case '>=':
      return fieldNum >= filterNum;
    case '<=':
      return fieldNum <= filterNum;
    default:
      return false;
  }
}

/**
 * Matches the `ouTreePicker` matcher's JSON-encoded
 * `{ exact, subtrees, totalCount }` matchValue shape (see
 * libs/filter-bar-selection-matchers/.../ou-tree-picker.matcher.ts).
 *
 * Mirrors the backend's condition builder (see
 * TypeORMDataRepository.buildOuTreePickerFindCondition): `exact` paths must
 * match exactly, `subtrees` paths match themselves or any descendant path
 * (`${prefix}/...`), and an empty exact+subtrees pair means "all OUs
 * selected" — no filtering.
 */
function matchOuTreePicker(fieldValue: unknown, filterValue: unknown): boolean {
  if (typeof filterValue !== 'string') {
    return false;
  }

  let parsed: { exact?: string[]; subtrees?: string[] };
  try {
    parsed = JSON.parse(filterValue);
  } catch {
    return false;
  }

  const exact = parsed?.exact ?? [];
  const subtrees = parsed?.subtrees ?? [];

  if (exact.length === 0 && subtrees.length === 0) {
    return true;
  }

  const fieldStr = String(fieldValue);
  if (exact.includes(fieldStr)) {
    return true;
  }
  return subtrees.some((prefix) => fieldStr === prefix || fieldStr.startsWith(`${prefix}/`));
}

/**
 * Matches the `dateTime` matcher's JSON-encoded `{ option, value }`
 * matchValue shape (see libs/filter-bar-matcher-configs/src/lib/date-time.config.ts).
 */
function matchDateTime(fieldValue: unknown, filterValue: unknown): boolean {
  if (typeof filterValue !== 'string') {
    return false;
  }
  const fieldDate = new Date(String(fieldValue));
  if (Number.isNaN(fieldDate.getTime())) {
    return false;
  }

  try {
    const parsed: {
      option: string;
      value: string | { start: string; end: string } | boolean;
    } = JSON.parse(filterValue);
    const now = Date.now();

    if (isRelativeDateTimeMatcherOption(parsed.option)) {
      // >= to match the MoreThanOrEqual the SQL layer emits for these same options.
      return fieldDate.getTime() >= now - relativeDateTimeLookbackMs[parsed.option];
    }

    switch (parsed.option) {
      case 'sinceDateTime':
        return fieldDate.getTime() >= new Date(parsed.value as string).getTime();
      case 'beforeDateTime':
        return fieldDate.getTime() < new Date(parsed.value as string).getTime();
      case 'betweenDateTime': {
        const range = parsed.value as { start: string; end: string };
        const start = new Date(range.start).getTime();
        const end = new Date(range.end).getTime();
        return fieldDate.getTime() >= start && fieldDate.getTime() <= end;
      }
      default:
        return false;
    }
  } catch {
    return false;
  }
}
